import { html, nothing } from "lit";
import type {
  LlmConnectDestination,
  LlmConnectState,
  LlmDiscoveredModel,
  LlmProviderCard,
} from "../controllers/llm-connect.ts";
import { providerStatusLabel } from "../controllers/llm-connect.ts";

export type LlmConnectProps = {
  state: LlmConnectState;
  providers: LlmProviderCard[];
  discovered: LlmDiscoveredModel[];
  defaultModel: string;
  connected: boolean;
  onSelectProvider: (id: string) => void;
  onApiKeyChange: (value: string) => void;
  onTest: () => void;
  onSave: () => void;
  onToggleModel: (modelId: string, enabled: boolean) => void;
  onDestination: (destination: LlmConnectDestination) => void;
  onSetDefault: (modelRef: string) => void;
};

export function renderLlmConnect(props: LlmConnectProps) {
  const selected =
    props.providers.find((provider) => provider.id === props.state.selectedProviderId) ??
    props.providers[0] ??
    null;
  return html`
    <div class="llm-connect" data-testid="llm-connect">
      <header class="llm-connect__intro">
        <h2 class="llm-connect__title">LLM Connect</h2>
        <p class="llm-connect__lede">
          Connect providers, install local models, and configure Jasiu's default LLM.
        </p>
      </header>
      <div class="llm-connect__grid">
        <aside class="card llm-connect__providers" aria-label="Providers">
          <div class="llm-connect__card-head">
            <div class="card-title">Providers</div>
            <div class="card-sub">Connect a cloud provider with an API key.</div>
          </div>
          <div class="llm-connect__provider-list">
            ${props.providers.map((provider) => {
              const active = selected?.id === provider.id;
              return html`
                <button
                  type="button"
                  class="llm-connect__provider ${active ? "is-active" : ""}"
                  aria-pressed=${active}
                  data-testid="llm-provider"
                  data-provider-id=${provider.id}
                  @click=${() => props.onSelectProvider(provider.id)}
                >
                  <span
                    class="llm-connect__provider-dot ${provider.connected ? "is-on" : ""}"
                  ></span>
                  <span>
                    <span class="llm-connect__provider-name">${provider.name}</span>
                    <span class="llm-connect__provider-status"
                      >${providerStatusLabel(provider)}</span
                    >
                  </span>
                </button>
              `;
            })}
          </div>
        </aside>
        <section class="card llm-connect__form">
          ${
            selected
              ? html`
                  <div class="llm-connect__card-head">
                    <div>
                      <div class="card-title">${selected.name}</div>
                      <div class="card-sub">Cloud API connection</div>
                    </div>
                    <span class="llm-connect__pill ${selected.connected ? "is-on" : ""}">
                      ${selected.connected ? "Connected" : "Ready"}
                    </span>
                  </div>
                  <label class="llm-connect__field">
                    <span>API key</span>
                    <input
                      type="password"
                      autocomplete="off"
                      data-testid="llm-connect-api-key"
                      placeholder=${selected.hasKey ? "Stored locally — enter a new key to replace it" : "Paste API key"}
                      .value=${props.state.apiKeyDraft}
                      @input=${(event: Event) =>
                        props.onApiKeyChange((event.target as HTMLInputElement).value)}
                    />
                  </label>
                  <p class="llm-connect__hint">
                    Stored locally and protected by the backend. Never displayed again in full.
                  </p>
                  <div class="llm-connect__actions">
                    <button
                      type="button"
                      class="btn"
                      data-testid="llm-connect-test"
                      ?disabled=${props.state.testing || !props.connected}
                      title="Check availability using the provider's own mechanism"
                      @click=${props.onTest}
                    >
                      ${props.state.testing ? "Testing…" : "Test connection"}
                    </button>
                    <button
                      type="button"
                      class="btn primary"
                      data-testid="llm-connect-save"
                      ?disabled=${props.state.saving || !props.connected || !props.state.apiKeyDraft.trim()}
                      @click=${props.onSave}
                    >
                      ${props.state.saving ? "Saving…" : "Save connection"}
                    </button>
                  </div>
                  ${
                    props.state.testMessage
                      ? html`
                          <div
                            class="callout ${props.state.testOk ? "" : "danger"}"
                            style="margin-top: 12px;"
                          >
                            ${props.state.testMessage}
                          </div>
                        `
                      : nothing
                  }
                  <label class="llm-connect__field">
                    <span>When this model is added</span>
                    <select
                      data-testid="llm-connect-destination"
                      .value=${props.state.destination}
                      @change=${(event: Event) =>
                        props.onDestination(
                          (event.target as HTMLSelectElement).value as LlmConnectDestination,
                        )}
                    >
                      <option value="unassigned">Not yet assigned</option>
                      <option value="role">Assign to role…</option>
                      <option value="default">Set as platform default</option>
                    </select>
                  </label>
                  <p class="llm-connect__hint">
                    Models are assigned to roles or set as the platform default. There is no
                    per-agent choice.
                  </p>
                  <dl class="llm-connect__meta">
                    <div>
                      <dt>API endpoint</dt>
                      <dd class="mono">${selected.baseUrl || "—"}</dd>
                    </div>
                    <div>
                      <dt>Discovered models</dt>
                      <dd>${props.discovered.length}</dd>
                    </div>
                  </dl>
                `
              : html`<p class="muted">Select a provider.</p>`
          }
          ${
            props.state.error
              ? html`<div class="callout danger" style="margin-top: 12px;">
                  ${props.state.error}
                </div>`
              : nothing
          }
        </section>
        <section class="card llm-connect__models" aria-label="Discovered models">
          <div class="llm-connect__card-head">
            <div>
              <div class="card-title">Discovered models</div>
              <div class="card-sub">Enable the models Jasiu can use.</div>
            </div>
          </div>
          <div class="llm-connect__model-list">
            ${
              props.discovered.length === 0
                ? html`<p class="muted">
                    Save a connection to discover models from this provider.
                  </p>`
                : props.discovered.map(
                    (model) => html`
                      <label class="llm-connect__model">
                        <input
                          type="checkbox"
                          .checked=${model.enabled}
                          @change=${(event: Event) =>
                            props.onToggleModel(
                              model.id,
                              (event.target as HTMLInputElement).checked,
                            )}
                        />
                        <span>
                          <span class="llm-connect__model-name">${model.name}</span>
                          <span class="llm-connect__model-id mono"
                            >${model.provider}/${model.id}</span
                          >
                        </span>
                        ${
                          props.defaultModel === `${model.provider}/${model.id}`
                            ? html`<span class="llm-connect__pill is-on">Default</span>`
                            : html`
                                <button
                                  type="button"
                                  class="btn btn--sm"
                                  title="Set as platform default"
                                  @click=${() => props.onSetDefault(`${model.provider}/${model.id}`)}
                                >
                                  Use as default
                                </button>
                              `
                        }
                      </label>
                    `,
                  )
            }
          </div>
          <p class="llm-connect__hint">
            The list is scrollable. Models are discovered from the provider, not hardcoded in the
            product. Platform default: ${props.defaultModel || "unset"}.
          </p>
        </section>
      </div>
    </div>
  `;
}
